// Registry of pages and their controllable search filters, used by the
// "الفلاتر الخاصة" (private filters) admin page and by useFilterVisibility.
//
// IMPORTANT: filters are hardcoded JSX in each page's Search component, so this
// registry is the MANUAL source of truth. Each entry's `key` MUST match:
//   1. the filter_key passed to isVisible('key') where that filter is wrapped in
//      the page's Search component, and
//   2. the filter_key stored in the backend filter_visibility_config table.
//
// To add another page later: add an entry here, wire its Search component with
// useFilterVisibility(pageKey), and wrap each filter block in isVisible(key).
export const FILTER_REGISTRY = [
    {
        pageKey: 'transactions_custom',
        pageLabel: 'مستندات الجهد الخارجي',
        // NOTE: only filters actually wrapped with <Gate name="..."> in
        // Transactions/Search.js are listed here, so every toggle controls a
        // real filter. To expose more filters, wrap them in Search.js and add
        // their {key,label} below.
        filters: [
            { key: 'unit', label: 'المركبة' },
            { key: 'account', label: 'العائدية او الحساب' },
            { key: 'is_governmental', label: 'نوع الجهد' },
            { key: 'municipality', label: 'البلديات' },
            { key: 'mizan_selected', label: 'الميزان' },
            { key: 'unit_type_selected', label: 'نوع المركبة' },
            { key: 'port_categories_selected', label: 'صنف نقطة التفريغ' },
            { key: 'groups_selected', label: 'المجاميع' },
            { key: 'sourceGeofence', label: 'نقطة التحميل' },
            { key: 'host_mizan_selected', label: 'الميزان المستضاف' },
            { key: 'hostingSourceGeofence', label: 'نقطة التحميل المستضافة' },
            { key: 'destinationGeofence', label: 'نقطة التفريغ' },
            { key: 'creationDate', label: 'وقت اصدار المستند' },
            { key: 'transaction_number', label: 'رقم المستند' },
            { key: 'weight_range', label: 'مدى الوزن (طن)' },
            { key: 'auditedByName', label: 'دقق من قبل' },
            { key: 'note', label: 'البحث في الملاحظات' },
            { key: 'transaction_status_selected', label: 'حالات التدقيق' },
            { key: 'weight_problem', label: 'مشاكل الاوزان' },
            { key: 'is_monitored', label: 'مراقبة' },
            { key: 'has_violation', label: 'الخروج عن المسار' },
            { key: 'is_complete', label: 'مكتمل' },
            { key: 'exceeded_allowed_time', label: 'تعدى الوقت المحدد' },
            { key: 'has_stop_violation', label: 'لديه توقفات' },
            { key: 'valid', label: 'ملتزم بكل شئ' },
            { key: 'destination_mismatch', label: 'مطابقة الوجهة' },
            { key: 'audited_by_type', label: 'جهة التدقيق' },
        ],
    },
    {
        pageKey: 'transactions_internal',
        pageLabel: 'مستندات الجهد الداخلي',
        // The internal page reuses the shared Transactions/Search.js (isInternal),
        // so these are the gated filters that render in internal mode.
        filters: [
            { key: 'unit', label: 'المركبة' },
            { key: 'account', label: 'العائدية' },
            { key: 'is_governmental', label: 'نوع الجهد' },
            { key: 'municipality', label: 'البلدية' },
            { key: 'mizan_selected', label: 'الميزان' },
            { key: 'unit_type_selected', label: 'نوع المركبة' },
            { key: 'groups_selected', label: 'المجاميع' },
            { key: 'sourceGeofence', label: 'نقطة التفريغ' },
            { key: 'host_mizan_selected', label: 'الميزان المستضاف' },
            { key: 'hostingSourceGeofence', label: 'نقطة التحميل المستضافة' },
            { key: 'transaction_number', label: 'رقم المستند' },
            { key: 'weight_range', label: 'مدى الوزن (طن)' },
            { key: 'weight_problem', label: 'مشاكل الاوزان' },
            { key: 'creationDate', label: 'وقت اصدار المستند' },
        ],
    },
    {
        pageKey: 'report_unit_transaction_summary',
        pageLabel: 'تقرير ملخص معاملات المركبة',
        // Filters are inline in ReportUnitTransactionSummary.js, wrapped in <Gate>.
        filters: [
            { key: 'unit', label: 'المركبة' },
            { key: 'account', label: 'العائدية او الحساب' },
            { key: 'is_governmental', label: 'نوع الجهد' },
            { key: 'municipality', label: 'البلديات' },
            { key: 'mizan_selected', label: 'الميزان' },
            { key: 'sourceGeofence', label: 'نقطة التحميل' },
            { key: 'unit_type_selected', label: 'نوع المركبة' },
            { key: 'destinationGeofence', label: 'نقطة التفريغ' },
            { key: 'transaction_status_selected', label: 'حالة التدقيق' },
            { key: 'groups_selected', label: 'المجاميع' },
            { key: 'weight_range', label: 'مدى الوزن (طن)' },
            { key: 'creationDate', label: 'تاريخ الانشاء' },
        ],
    },
    {
        pageKey: 'report_unit_transaction_summary_internal',
        pageLabel: 'ملخص حركات مركبات الجهد الداخلي',
        // Same component as the external summary (ReportUnitTransactionSummary.js with
        // isInternal) — minus the destination / audit-status filters it hides.
        filters: [
            { key: 'unit', label: 'المركبة' },
            { key: 'account', label: 'العائدية او الحساب' },
            { key: 'is_governmental', label: 'نوع الجهد' },
            { key: 'municipality', label: 'البلديات' },
            { key: 'mizan_selected', label: 'الميزان' },
            { key: 'sourceGeofence', label: 'نقطة التفريغ' },
            { key: 'unit_type_selected', label: 'نوع المركبة' },
            { key: 'groups_selected', label: 'المجاميع' },
            { key: 'weight_range', label: 'مدى الوزن (طن)' },
            { key: 'creationDate', label: 'تاريخ الانشاء' },
        ],
    },
]

export const getRegistryPage = (pageKey) => FILTER_REGISTRY.find(p => p.pageKey === pageKey)
